import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { TopLevelAnalysisResult } from 'libs/end-to-end/src/top-level-analysis';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { TopLevelAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TopLevelAnalysisDefinition';
import { generateQueryPartOnObservation } from 'libs/end-to-end/src/observation-upload-type';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { Treatments } from 'libs/end-to-end/src/treatment';
import { UpdateTreatments } from 'libs/end-to-end/src/treatment';
import { createTreatmentKey } from 'libs/end-to-end/src/treatment';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { useCustomVizAutoload } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { useSelectedOrganizationId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedOrganizationId';
import { useSelectedDatabaseId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseId';
import { useSelectedDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseRevisionId';

interface RemoveTreatmentOptions {
  onBeforeRemove?: (treatmentKey: string) => Promise<void>;
  onRemove?: (treatmentKey: string) => Promise<void>;
}

export interface UpdateTreatmentOptions {
  onSuccess?: () => Promise<void>;
  onError?: (error: BaseError) => Promise<void>;
}

interface ToggleTreatmentOptions {
  onBeforeToggle?: (treatmentKey: string, isActive: boolean) => Promise<void>;
  onToggle?: (treatmentKey: string, isActive: boolean) => Promise<void>;
}

export interface ObservationContext {
  selectedOrganizationId: string | null;
  myObservation: MyObservation | null;
  removeTreatment: (
    treatmentKey: string,
    options?: RemoveTreatmentOptions,
  ) => void;
  toggleTreatment: (
    treatmentKey: string,
    isActive: boolean,
    options?: ToggleTreatmentOptions,
  ) => void;
  updateTreatments: (
    treatmentUpdates: Treatment[],
    options?: UpdateTreatmentOptions,
  ) => void;
  failedToLoadObservation: boolean;
  staleResultShowing: boolean;
}

export const ObservationContext = createContext<ObservationContext>({
  selectedOrganizationId: null,
  myObservation: null,
  removeTreatment: () => {},
  toggleTreatment: () => {},
  updateTreatments: () => {},
  failedToLoadObservation: false,
  staleResultShowing: false,
});

interface ObservationRendererProps {
  children: React.ReactNode;
  selectedOrganization: string | null;
  selectedDatabase: string | null;
  selectedDatabaseRevision: string | null;
}

export interface ObservationHook {
  selectedOrganizationId: string | null;
  myObservation: MyObservation | null;
  failedToLoadObservation: boolean;
  removeTreatment: (
    treatmentKey: string,
    options?: RemoveTreatmentOptions,
  ) => void;
  toggleTreatment: (
    treatmentKey: string,
    isActive: boolean,
    options?: ToggleTreatmentOptions,
  ) => void;
  updateTreatments: (
    treatmentUpdates: Treatment[],
    options?: UpdateTreatmentOptions,
  ) => void;
  staleResultShowing: boolean;
}

export const useObservation = (): ObservationHook => {
  const context = useContext(ObservationContext);

  return useMemo(() => {
    const {
      selectedOrganizationId,
      myObservation,
      removeTreatment,
      toggleTreatment,
      updateTreatments,
      failedToLoadObservation,
      staleResultShowing,
    } = context;

    return {
      selectedOrganizationId,
      myObservation,
      removeTreatment,
      toggleTreatment,
      updateTreatments,
      failedToLoadObservation,
      staleResultShowing,
    };
  }, [context]);
};

const isAutoStartVizEnabled = true;

export const ObservationRenderer = ({
  children,
  selectedDatabaseRevision,
  selectedOrganization,
  selectedDatabase,
}: ObservationRendererProps) => {
  const { myUser, userAuthenticatedApiCall } = useUser();

  const [myObservation, setMyObservation] = useState<MyObservation | null>(
    null,
  );

  const [failedToLoadObservation, setFailedToLoadObservation] = useState(false);

  const analytics = useAnalytics();

  const customVizContext = useCustomViz();

  const canAnalyse = useMemo(() => {
    return !(
      !myUser ||
      !myUser.organization ||
      !selectedOrganization ||
      !selectedDatabase ||
      !selectedDatabaseRevision ||
      !isAutoStartVizEnabled
    );
  }, [
    myUser,
    selectedDatabase,
    selectedDatabaseRevision,
    selectedOrganization,
  ]);

  const onAnalyze = useCallback(() => {
    if (
      !myUser ||
      !myUser.organization ||
      !selectedOrganization ||
      !selectedDatabase ||
      !selectedDatabaseRevision
    ) {
      return;
    }

    if (!isAutoStartVizEnabled) {
      return;
    }

    setFailedToLoadObservation(false);

    customVizContext.doViz({
      customVizId: 'topLevelAnalysis',
      observationLocationType: {
        type: 'db-revision',
        databaseId: selectedDatabase,
        databaseRevisionId: selectedDatabaseRevision,
      },
      organizationId: selectedOrganization,
      inputs: {
        dropdowns: {},
        extras: {},
      },
    });
  }, [
    customVizContext,
    myUser,
    selectedDatabase,
    selectedDatabaseRevision,
    selectedOrganization,
  ]);

  const { data, hasFailed } = useCustomVizAutoload({
    canAnalyse,
    onAnalyze,
    context: customVizContext,
    ResultZodType: TopLevelAnalysisResult,
  });

  // When the observation fails to load, we want to show an error message.
  useEffect(() => {
    if (hasFailed) {
      setFailedToLoadObservation(true);
    }
  }, [hasFailed]);

  useAsyncEffect(
    // eslint-disable-next-line complexity, react-hooks/exhaustive-deps
    async (isAlive) => {
      if (
        !isAlive() ||
        !myUser ||
        !myUser.organization ||
        !selectedOrganization ||
        !selectedDatabase ||
        !selectedDatabaseRevision
      ) {
        return;
      }

      if (isAutoStartVizEnabled && !data) {
        return;
      }

      const observationResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/me/observation?${generateQueryPartOnObservation(
          {
            type: 'db-revision',
            databaseId: selectedDatabase,

            databaseRevisionId: selectedDatabaseRevision,
          },
        )}&organizationId=${selectedOrganization}`,
        responseSchema: MyObservation,
      });
      if (!observationResult || observationResult.resultType === 'err') {
        setMyObservation(null);
        setFailedToLoadObservation(true);
        return;
      }

      setMyObservation(observationResult.value);

      analytics.registerDatabase({
        databaseId: observationResult.value.type.databaseId,
        databaseName: 'Observation',
        databaseRevisionId: observationResult.value.type.databaseRevisionId,
      });
    },
    [
      myUser,
      userAuthenticatedApiCall,
      data,
      analytics,
      selectedOrganization,
      selectedDatabase,
      selectedDatabaseRevision,
    ],
  );

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const removeTreatment = useAsyncCallback(
    // eslint-disable-next-line complexity
    async (isAlive, treatmentKey: string, options?: RemoveTreatmentOptions) => {
      if (
        !isAlive() ||
        !myUser ||
        !myUser.organization ||
        !myObservation ||
        !myObservation.treatments
      ) {
        return;
      }

      const existingTreatment = myObservation.treatments.find(
        (treatment) => createTreatmentKey(treatment) === treatmentKey,
      );

      if (!existingTreatment) {
        return;
      }

      const newTreatment: Treatment = {
        ...existingTreatment,
        status: 'Available',
      };

      setMyObservation((prev) => {
        if (!prev || !prev.treatments) {
          return prev;
        }
        return {
          ...prev,
          treatments: prev.treatments.map((treatment) => {
            return createTreatmentKey(treatment) === treatmentKey
              ? newTreatment
              : treatment;
          }),
        };
      });

      if (options?.onBeforeRemove) {
        await options.onBeforeRemove(treatmentKey);
      }

      if (!isAlive()) {
        return;
      }

      const updateTreatmentRequest: UpdateTreatments = {
        treatments: [newTreatment],
      };

      const updateTreatmentResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/me/treatments?organizationId=${selectedOrganization}&${generateQueryPartOnObservation(
          myObservation.type,
        )}`,
        responseSchema: Treatments,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(updateTreatmentRequest),
        },
      });

      if (!updateTreatmentResult || !isAlive()) {
        return;
      }

      if (options?.onRemove) {
        await options.onRemove(treatmentKey);
      }
    },
    [myUser, myObservation, userAuthenticatedApiCall, selectedOrganization],
  );

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const toggleTreatment = useAsyncCallback(
    async (
      isAlive,
      treatmentKey: string,
      isActive: boolean,
      options?: ToggleTreatmentOptions,
    ) => {
      if (
        !isAlive() ||
        !myUser ||
        !myUser.organization ||
        !myObservation ||
        !myObservation.treatments
      ) {
        return;
      }

      const existingTreatment = myObservation.treatments.find(
        (treatment) => createTreatmentKey(treatment) === treatmentKey,
      );

      if (!existingTreatment) {
        return;
      }

      const newTreatment: Treatment = {
        ...existingTreatment,
        status: isActive ? 'Active' : 'Selected',
      };

      setMyObservation((prev) => {
        if (!prev || !prev.treatments) {
          return prev;
        }

        return {
          ...prev,
          treatments: prev.treatments.map((treatment) => {
            return createTreatmentKey(treatment) === treatmentKey
              ? newTreatment
              : treatment;
          }),
        };
      });

      if (options?.onBeforeToggle) {
        await options.onBeforeToggle(treatmentKey, isActive);
      }

      if (!isAlive()) {
        return;
      }

      const updateTreatmentRequest: UpdateTreatments = {
        treatments: [newTreatment],
      };

      const updateTreatmentResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/me/treatments?organizationId=${selectedOrganization}&${generateQueryPartOnObservation(
          myObservation.type,
        )}`,
        responseSchema: Treatments,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(updateTreatmentRequest),
        },
      });

      if (!updateTreatmentResult || !isAlive()) {
        return;
      }

      if (options?.onToggle) {
        await options.onToggle(treatmentKey, isActive);
      }
    },
    [myUser, myObservation, userAuthenticatedApiCall, selectedOrganization],
  );

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const updateTreatments = useAsyncCallback(
    async (
      isAlive,
      treatmentUpdates: Treatment[],
      options?: UpdateTreatmentOptions,
    ) => {
      if (
        !isAlive() ||
        !myUser ||
        !myUser.organization ||
        !myObservation ||
        !myObservation.treatments
      ) {
        return;
      }

      const updateTreatmentsRequest: UpdateTreatments = {
        treatments: treatmentUpdates,
      };

      const updateTreatmentsResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/me/treatments?organizationId=${selectedOrganization}&${generateQueryPartOnObservation(
          myObservation.type,
        )}`,
        responseSchema: Treatments,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(updateTreatmentsRequest),
        },
      });

      if (!isAlive() || !updateTreatmentsResult) {
        return;
      }

      if (updateTreatmentsResult.resultType === 'err') {
        if (options?.onError) {
          await options.onError(updateTreatmentsResult.error);
        }

        return;
      }

      if (options?.onSuccess) {
        await options.onSuccess();
      }

      setMyObservation((prev) => {
        if (!prev || !prev.treatments) {
          return prev;
        }

        return {
          ...prev,
          treatments: prev.treatments.map((existingTreatment) => {
            const updatedTreatment = treatmentUpdates.find(
              (treatmentUpdate) =>
                createTreatmentKey(treatmentUpdate) ===
                createTreatmentKey(existingTreatment),
            );

            if (!updatedTreatment) {
              return existingTreatment;
            }

            return updatedTreatment;
          }),
        };
      });
    },
    [myUser, myObservation, userAuthenticatedApiCall, selectedOrganization],
  );

  const staleResultShowing = useMemo(() => {
    return (
      !!data && customVizContext.status === 'loading-viz-with-stale-result'
    );
  }, [customVizContext, data]);

  const value = useMemo(
    () => ({
      selectedOrganizationId: selectedOrganization,
      myObservation,
      failedToLoadObservation,
      removeTreatment,
      toggleTreatment,
      updateTreatments,
      staleResultShowing,
    }),
    [
      selectedOrganization,
      myObservation,
      failedToLoadObservation,
      removeTreatment,
      toggleTreatment,
      updateTreatments,
      staleResultShowing,
    ],
  );

  return (
    <ObservationContext.Provider value={value}>
      {children}
    </ObservationContext.Provider>
  );
};

export interface ObservationWrapperHook {
  observationLocation: ObservationLocationType | null;
}
export interface ObservationWrapperContext {
  observationLocation: ObservationLocationType | null;
}
const ObservationWrapperContext = createContext<ObservationWrapperContext>({
  observationLocation: null,
});

export const ObservationWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const selectedOrganization = useSelectedOrganizationId();
  const selectedDatabase = useSelectedDatabaseId();
  const selectedDatabaseRevision = useSelectedDatabaseRevisionId();

  return (
    <ObservationWrapperInternal
      selectedDatabase={selectedDatabase}
      selectedDatabaseRevision={selectedDatabaseRevision}
      selectedOrganization={selectedOrganization}
    >
      {children}
    </ObservationWrapperInternal>
  );
};

export const ObservationWrapperInternal = ({
  children,
  selectedOrganization,
  selectedDatabase,
  selectedDatabaseRevision,
}: {
  children: React.ReactNode;
  selectedOrganization: string | null;
  selectedDatabase: string | null;
  selectedDatabaseRevision: string | null;
}) => {
  const { myUser, userAuthenticatedApiCall } = useUser();

  const observationLocation: ObservationLocationType | null = useMemo(() => {
    if (
      !myUser ||
      !myUser.organization ||
      !selectedOrganization ||
      !selectedDatabase ||
      !selectedDatabaseRevision
    ) {
      return null;
    }

    return {
      type: 'db-revision',
      databaseId: selectedDatabase,
      databaseRevisionId: selectedDatabaseRevision,
    };
  }, [
    myUser,
    selectedDatabase,
    selectedDatabaseRevision,
    selectedOrganization,
  ]);

  if (!observationLocation || !selectedOrganization) {
    return <div>{children}</div>;
  }

  return (
    <CustomViz
      observation={observationLocation}
      organizationId={selectedOrganization}
      customVizDefinition={TopLevelAnalysisCustomVizDefinition}
      userAuthenticatedApiCall={userAuthenticatedApiCall}
    >
      <ObservationRenderer
        selectedOrganization={selectedOrganization}
        selectedDatabaseRevision={selectedDatabaseRevision}
        selectedDatabase={selectedDatabase}
      >
        {children}
      </ObservationRenderer>
    </CustomViz>
  );
};
